import { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ExportButton } from "@/components/ExportButton";
import { SearchableSelect } from "@/components/SearchableSelect";
import { useDateRange } from "@/hooks/useDateRange";
import { exportElementToPdf } from "@/lib/exportPdf";
import { Loader2, Download } from "lucide-react";

const rankBadge = (rank) =>
  rank === 1 ? "bg-amber-400 text-white" : rank === 2 ? "bg-slate-300 text-slate-800" : rank === 3 ? "bg-amber-700 text-white" : "bg-slate-100 text-slate-600";

const pctColor = (p) => (p >= 75 ? "text-emerald-700" : p >= 50 ? "text-amber-600" : "text-red-500");

const districtOptionsOf = (munis) =>
  [{ label: "All Districts", value: "" }, ...[...new Set((munis || []).map((m) => m.district).filter(Boolean))].sort((a, b) => a.localeCompare(b)).map((d) => ({ label: d, value: d }))];

const popMapOf = (munis) => {
  const m = {};
  (munis || []).forEach((x) => {
    const nk = (x.name || "").trim().toLowerCase();
    if (m[nk] === undefined) m[nk] = x.population;
  });
  return m;
};

const buildTypeGroups = (muniRows, pm) => {
  const b = { corp: [], high: [], low: [], comm: [] };
  (muniRows || []).forEach((r) => {
    const t = (r.type || "").toUpperCase();
    if (t.includes("CORPORATION")) b.corp.push(r);
    else if (t.includes("COMMITTEE")) b.comm.push(r);
    else if (t.includes("COUNCIL")) {
      const p = pm[(r.name || "").trim().toLowerCase()];
      (p != null && p >= 100000 && p <= 300000 ? b.high : b.low).push(r);
    }
  });
  const mk = (arr) => arr.slice(0, 3).map((r, i) => ({ ...r, rank: i + 1 }));
  return [
    { key: "corp", title: "Municipal Corporation", rows: mk(b.corp) },
    { key: "council_high", title: "Municipal Council (1 Lakh – 3 Lakh)", rows: mk(b.high) },
    { key: "council_low", title: "Municipal Council (50,000 – 1 Lakh)", rows: mk(b.low) },
    { key: "comm", title: "Municipal Committee", rows: mk(b.comm) },
  ].filter((g) => g.rows.length > 0);
};

const MiniRow = ({ rank, title, sub, percent, score, max, testId }) => (
  <div className="flex items-center gap-3 px-4 py-2.5" data-testid={testId}>
    <span className={`inline-flex items-center justify-center h-7 w-7 shrink-0 rounded-full text-xs font-bold ${rankBadge(rank)}`}>{rank}</span>
    <div className="flex-1 min-w-0">
      <p className="font-medium text-slate-800 truncate leading-6">{title}</p>
      {sub ? <p className="text-xs text-slate-500 capitalize truncate leading-5">{sub}</p> : null}
    </div>
    <div className="text-right shrink-0">
      <p className={`font-semibold tabular-nums ${pctColor(percent)}`}>{percent}%</p>
      <p className="text-xs text-slate-400 tabular-nums">{score}/{max}</p>
    </div>
  </div>
);

const MiniCard = ({ title, children, testId }) => (
  <Card className="border-slate-200 shadow-sm overflow-hidden" data-testid={testId}>
    <div className="px-4 py-3 bg-emerald-600 text-white font-heading font-semibold text-sm capitalize">{typeof title === "string" ? title.toLowerCase() : title}</div>
    <div className="divide-y divide-slate-100">{children}</div>
  </Card>
);

const muniRow = (r, i) => ({ rank: r.rank ?? i + 1, title: r.name, sub: r.type, percent: r.percent, score: r.score, max: r.max });
const wardRow = (r, i) => ({ rank: r.rank ?? i + 1, title: `${r.name} · Ward ${r.ward_no ?? "—"}`, sub: (r.district || "").toLowerCase(), percent: r.percent, score: r.score, max: r.max });

function Podium({ topMunis, topWards, groups, prefix }) {
  const empty = <div className="px-4 py-6 text-center text-sm text-slate-400">No data.</div>;
  return (
    <>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <MiniCard title="Top 3 Municipalities" testId={`${prefix}-top-munis`}>
          {topMunis.length ? topMunis.map((r, i) => <MiniRow key={i} {...muniRow(r, i)} testId={`${prefix}-muni-${i + 1}`} />) : empty}
        </MiniCard>
        <MiniCard title="Top 3 Wards" testId={`${prefix}-top-wards`}>
          {topWards.length ? topWards.map((r, i) => <MiniRow key={i} {...wardRow(r, i)} testId={`${prefix}-ward-${i + 1}`} />) : empty}
        </MiniCard>
      </div>
      <h3 className="font-heading text-base font-semibold text-slate-800 mt-8 mb-4">Top 3 per Municipal Type</h3>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {groups.length ? groups.map((g) => (
          <MiniCard key={g.key} title={g.title} testId={`${prefix}-type-${g.key}`}>
            {g.rows.map((r, i) => <MiniRow key={i} {...muniRow(r, i)} sub={(r.district || "").toLowerCase() || r.type} testId={`${prefix}-type-${g.key}-${i + 1}`} />)}
          </MiniCard>
        )) : empty}
      </div>
    </>
  );
}

function WardFilterTable({ wards, munis, prefix }) {
  const [district, setDistrict] = useState("");
  const [muni, setMuni] = useState("");
  const dOpts = useMemo(() => districtOptionsOf(munis), [munis]);
  const mOpts = useMemo(() => {
    const list = (munis || []).filter((m) => !district || m.district === district);
    return [{ label: "All Municipalities", value: "" }, ...[...new Set(list.map((m) => m.name))].sort((a, b) => a.localeCompare(b)).map((n) => ({ label: n, value: n }))];
  }, [munis, district]);
  const rows = useMemo(() => wards
    .filter((w) => (!district || (w.district || "") === district) && (!muni || w.name === muni))
    .slice(0, 10), [wards, district, muni]);
  const exportRows = rows.map((r, i) => ({ rank: i + 1, municipality: r.name, district: r.district, ward: r.ward_no, score: r.score, max: r.max, percent: r.percent }));

  return (
    <Card className="border-slate-200 shadow-sm overflow-hidden mt-8" data-testid={`${prefix}-wardfilter-card`}>
      <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between flex-wrap gap-3">
        <span className="font-heading font-bold text-slate-800">Top 10 Wards (by filter)</span>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="w-48"><SearchableSelect value={district} onChange={(v) => { setDistrict(v); setMuni(""); }} options={dOpts} placeholder="All Districts" testId={`${prefix}-wf-district`} /></div>
          <div className="w-56"><SearchableSelect value={muni} onChange={setMuni} options={mOpts} placeholder="All Municipalities" testId={`${prefix}-wf-muni`} /></div>
          <ExportButton items={exportRows} filename={`${prefix}-top10-wards`} testId={`${prefix}-wf-export`} />
        </div>
      </div>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead className="w-14">#</TableHead><TableHead>Municipality</TableHead><TableHead>District</TableHead>
            <TableHead className="text-right">Ward</TableHead><TableHead className="text-right">Score</TableHead><TableHead className="text-right">%</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center py-10 text-slate-400">No wards match this filter.</TableCell></TableRow>
            ) : rows.map((r, i) => (
              <TableRow key={i} className="hover:bg-slate-50" data-testid={`${prefix}-wf-row-${i + 1}`}>
                <TableCell className="font-semibold text-slate-500">{i + 1}</TableCell>
                <TableCell className="font-medium text-slate-800">{r.name}</TableCell>
                <TableCell className="capitalize text-slate-600">{(r.district || "—").toLowerCase()}</TableCell>
                <TableCell className="text-right text-slate-600">{r.ward_no ?? "—"}</TableCell>
                <TableCell className="text-right tabular-nums text-slate-800">{r.score}/{r.max}</TableCell>
                <TableCell className={`text-right font-semibold ${pctColor(r.percent)}`}>{r.percent}%</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </Card>
  );
}

function StateWardTable({ wards, prefix }) {
  const rows = wards.slice(0, 20);
  const exportRows = rows.map((r) => ({ rank: r.rank, municipality: r.name, district: r.district, ward: r.ward_no, score: r.score, max: r.max, percent: r.percent }));
  return (
    <Card className="border-slate-200 shadow-sm overflow-hidden mt-8" data-testid={`${prefix}-statewards-card`}>
      <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
        <span className="font-heading font-bold text-slate-800">Top 20 Wards (State Level)</span>
        <ExportButton items={exportRows} filename={`${prefix}-top20-state-wards`} testId={`${prefix}-sw-export`} />
      </div>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead className="w-14">Rank</TableHead><TableHead>Municipality</TableHead><TableHead>District</TableHead>
            <TableHead className="text-right">Ward</TableHead><TableHead className="text-right">Score</TableHead><TableHead className="text-right">%</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center py-10 text-slate-400">No data.</TableCell></TableRow>
            ) : rows.map((r, i) => (
              <TableRow key={i} className="hover:bg-slate-50" data-testid={`${prefix}-sw-row-${r.rank}`}>
                <TableCell><span className={`inline-flex items-center justify-center h-7 w-7 rounded-full text-xs font-bold ${rankBadge(r.rank)}`}>{r.rank}</span></TableCell>
                <TableCell className="font-medium text-slate-800">{r.name}</TableCell>
                <TableCell className="capitalize text-slate-600">{(r.district || "—").toLowerCase()}</TableCell>
                <TableCell className="text-right text-slate-600">{r.ward_no ?? "—"}</TableCell>
                <TableCell className="text-right tabular-nums text-slate-800">{r.score}/{r.max}</TableCell>
                <TableCell className={`text-right font-semibold ${pctColor(r.percent)}`}>{r.percent}%</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </Card>
  );
}

function VerticalsTable({ munis, params }) {
  const [district, setDistrict] = useState("");
  const [muni, setMuni] = useState("");
  const [data, setData] = useState({ verticals: [], total_marks: 0, total_max: 0, overall_percent: 0 });
  const [loading, setLoading] = useState(false);
  const dOpts = useMemo(() => districtOptionsOf(munis), [munis]);
  const mOpts = useMemo(() => {
    const list = (munis || []).filter((m) => !district || m.district === district);
    return [{ label: "All Municipalities", value: "" }, ...[...new Set(list.map((m) => m.name))].sort((a, b) => a.localeCompare(b)).map((n) => ({ label: n, value: n }))];
  }, [munis, district]);

  useEffect(() => {
    setLoading(true);
    api.get("/scoring/verticals", { params: { ...(district ? { district } : {}), ...(muni ? { municipality: muni } : {}), ...params } })
      .then(({ data }) => setData(data)).catch(() => {}).finally(() => setLoading(false));
  }, [district, muni, params]);

  return (
    <Card className="border-slate-200 shadow-sm overflow-hidden mt-8" data-testid="mon-verticals-card">
      <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between flex-wrap gap-3">
        <span className="font-heading font-bold text-slate-800">13 Verticals — Marks &amp; Percentage</span>
        <div className="flex items-center gap-3 flex-wrap">
          {loading && <Loader2 className="h-4 w-4 animate-spin text-emerald-600" />}
          <div className="w-48"><SearchableSelect value={district} onChange={(v) => { setDistrict(v); setMuni(""); }} options={dOpts} placeholder="All Districts" testId="mon-vert-district" /></div>
          <div className="w-56"><SearchableSelect value={muni} onChange={setMuni} options={mOpts} placeholder="All Municipalities" testId="mon-vert-muni" /></div>
          <ExportButton items={data.verticals} filename="monitoring-verticals" testId="mon-vert-export" />
        </div>
      </div>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead className="w-14">#</TableHead><TableHead>Vertical</TableHead>
            <TableHead className="text-right">Marks</TableHead><TableHead className="text-right">Max</TableHead><TableHead className="text-right">%</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {(data.verticals || []).map((v, i) => (
              <TableRow key={v.key} className="hover:bg-slate-50" data-testid={`mon-vert-row-${v.key}`}>
                <TableCell className="text-slate-500">{i + 1}</TableCell>
                <TableCell className="font-medium text-slate-800">{v.label}</TableCell>
                <TableCell className="text-right tabular-nums text-slate-800">{v.marks}</TableCell>
                <TableCell className="text-right text-slate-500">{v.max}</TableCell>
                <TableCell className={`text-right font-semibold ${pctColor(v.percentage)}`}>{v.percentage}%</TableCell>
              </TableRow>
            ))}
            {(data.verticals || []).length > 0 && (
              <TableRow className="bg-slate-50 font-semibold" data-testid="mon-vert-total">
                <TableCell />
                <TableCell className="text-slate-900">Total</TableCell>
                <TableCell className="text-right tabular-nums text-slate-900">{data.total_marks}</TableCell>
                <TableCell className="text-right text-slate-600">{data.total_max}</TableCell>
                <TableCell className={`text-right ${pctColor(data.overall_percent)}`}>{data.overall_percent}%</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </Card>
  );
}

function AssessmentSection({ munis }) {
  const { params, controls } = useDateRange("assessment-range", "ytd");
  const [categories, setCategories] = useState([]);
  const [wards, setWards] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get("/rankings", { params }).then(({ data }) => {
      setCategories(data.categories || []);
      setWards((data.wards || []).map((w) => ({ name: w.area, district: w.district, ward_no: w.ward_no, score: w.score, max: w.max, percent: w.percent, rank: w.rank })));
    }).catch(() => {}).finally(() => setLoading(false));
  }, [params]);

  const muniRows = useMemo(() => {
    const all = [];
    (categories || []).forEach((c) => (c.rows || []).forEach((r) => all.push({ name: r.area, type: c.name || c.label, score: r.score, max: r.max, percent: r.percent })));
    return all;
  }, [categories]);
  const topMunis = useMemo(() => [...muniRows].sort((a, b) => (b.percent - a.percent) || (b.score - a.score)).slice(0, 3).map((r, i) => ({ ...r, rank: i + 1 })), [muniRows]);
  const groups = useMemo(() => buildTypeGroups(muniRows, popMapOf(munis)), [muniRows, munis]);

  return (
    <div data-testid="assessment-section">
      <div className="mb-6 pdf-hide">{controls}</div>
      {loading ? <div className="flex h-48 items-center justify-center"><Loader2 className="h-7 w-7 animate-spin text-emerald-600" /></div> : (
        <>
          <Podium topMunis={topMunis} topWards={wards.slice(0, 3)} groups={groups} prefix="asmt" />
          <WardFilterTable wards={wards} munis={munis} prefix="asmt" />
          <StateWardTable wards={wards} prefix="asmt" />
        </>
      )}
    </div>
  );
}

function MonitoringSection({ munis }) {
  const { params, controls } = useDateRange("monitoring-range", "ytd");
  const [data, setData] = useState({ ulb: [], ward: [], max_score: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get("/scoring", { params }).then(({ data }) => setData(data)).catch(() => {}).finally(() => setLoading(false));
  }, [params]);

  const muniRows = useMemo(() => (data.ulb || []).map((r) => ({ name: r.municipality_name, district: r.district, type: r.municipality_type, score: r.score, max: data.max_score, percent: r.percent, rank: r.rank })), [data]);
  const wards = useMemo(() => (data.ward || []).map((r) => ({ name: r.municipality_name, district: r.district, ward_no: r.ward_no, score: r.score, max: data.max_score, percent: r.percent, rank: r.rank })), [data]);
  const groups = useMemo(() => buildTypeGroups(muniRows, popMapOf(munis)), [muniRows, munis]);

  return (
    <div data-testid="monitoring-section">
      <div className="mb-6 pdf-hide">{controls}</div>
      {loading ? <div className="flex h-48 items-center justify-center"><Loader2 className="h-7 w-7 animate-spin text-emerald-600" /></div> : (
        <>
          <Podium topMunis={muniRows.slice(0, 3)} topWards={wards.slice(0, 3)} groups={groups} prefix="mon" />
          <WardFilterTable wards={wards} munis={munis} prefix="mon" />
          <StateWardTable wards={wards} prefix="mon" />
          <VerticalsTable munis={munis} params={params} />
        </>
      )}
    </div>
  );
}

export default function Leaderboard() {
  const [munis, setMunis] = useState([]);
  const [tab, setTab] = useState("assessment");
  const [exporting, setExporting] = useState(false);
  const pageRef = useRef(null);
  const tabLabel = tab === "assessment" ? "Assessment Rankings" : "Monitoring Rankings (13 Verticals)";

  useEffect(() => {
    api.get("/municipalities").then(({ data }) => setMunis(data)).catch(() => {});
  }, []);

  const downloadPdf = async () => {
    setExporting(true);
    await new Promise((r) => setTimeout(r, 60)); // let PDF-only section title render
    try { await exportElementToPdf(pageRef.current, `smh-leaderboard-${tab}`); } finally { setExporting(false); }
  };

  return (
    <div data-testid="leaderboard-page" ref={pageRef}>
      <div className="mb-6 flex justify-between items-start gap-4 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-emerald-600 font-medium">Rankings</p>
          <h1 className="font-heading text-2xl font-bold text-slate-900 tracking-tight">Leaderboard</h1>
          <p className="text-sm text-slate-500 mt-1">Assessment and Monitoring (13 Verticals) rankings across the state.</p>
        </div>
        <Button variant="outline" onClick={downloadPdf} disabled={exporting} data-testid="leaderboard-pdf-btn" className="pdf-hide">
          {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
          <span className="ml-2">PDF</span>
        </Button>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList className="mb-6 pdf-hide" data-testid="leaderboard-tabs">
          <TabsTrigger value="assessment" data-testid="tab-assessment">Assessment Rankings</TabsTrigger>
          <TabsTrigger value="monitoring" data-testid="tab-monitoring">Monitoring Rankings (13 Verticals)</TabsTrigger>
        </TabsList>
        {exporting && (
          <h2 className="font-heading text-xl font-bold text-slate-900 mb-6" data-testid="pdf-section-title">{tabLabel}</h2>
        )}
        <TabsContent value="assessment"><AssessmentSection munis={munis} /></TabsContent>
        <TabsContent value="monitoring"><MonitoringSection munis={munis} /></TabsContent>
      </Tabs>
    </div>
  );
}
